import {DEFAULT_BACKGROUND_DATA} from "../data/backgrounds.js";
import {isDraftUploadVariation} from "./bgIdentity";

/**
 * Pure background-transform derivation (issue #528, 2a).
 *
 * The displayed crop for a (background variation, template) is DERIVED by fixed
 * precedence — not written into the images context by whichever async fetch
 * resolves last (the old three-writer race). Precedence, highest first:
 *
 *   1. userAdjustment      — an explicit drag / tile-pick this session
 *   2. resolvedScene       — the server-resolved default-scene cascade (1c)
 *   3. productScene        — ALL-products banners only: the banner's product's own
 *                            resolved scene, keyed by the canonical template id
 *   4. catalogTransform    — the v3 background catalog (1a): the crop an admin
 *                            stored, relayed verbatim
 *   5. legacyDefault       — DEFAULT_BACKGROUND_DATA, the pre-v3 hand-tuned
 *                            table, for a variation with no stored scene at all
 *
 * Nothing below that: a template with no crop in any layer HAS no crop, and the
 * caller renders the image at natural size, top-left — exactly what the legacy
 * path does. A DERIVED cover-fit used to sit here and in the catalog, and it
 * diverged from production, because the box a crop lives in is the RENDERED
 * background box (per channel, per layout), not the template's declared size.
 *
 * A late-arriving fetch can no longer overwrite a value — it only fills its own
 * slot in the chain. All functions here are pure and framework-free.
 */

/** The runtime transform shape the editor consumes. */
const DEFAULT_FLIP = {x: false, y: false};

/** Normalize any partial transform into the full {x,y,scale,rotate,flip} shape. */
function normalize(t) {
  if (!t) return null;
  const {x, y, scale} = t;
  if (typeof x !== "number" || typeof y !== "number" || typeof scale !== "number") return null;
  return {
    x,
    y,
    scale,
    rotate: typeof t.rotate === "number" ? t.rotate : 0,
    flip: t.flip && typeof t.flip === "object" ? t.flip : {...DEFAULT_FLIP},
  };
}

/**
 * Extract a template's transform from a resolved default scene's per-field
 * records (bgX/bgY/bgScale/bgRotate/bgFlip). Returns null when the scene has no
 * complete transform for that template.
 */
export function transformFromScene(scene, templateId) {
  if (!scene) return null;
  return normalize({
    x: scene.bgX?.[templateId],
    y: scene.bgY?.[templateId],
    scale: scene.bgScale?.[templateId],
    rotate: scene.bgRotate?.[templateId],
    flip: scene.bgFlip?.[templateId],
  });
}

/**
 * Channels whose every template renders its OWN background asset and never the
 * gallery/catalog artwork:
 *
 *   - livestream — `customBgs[id] || LIVESTREAM_BGS[id]`, a per-frame pre-cropped
 *     gradient SVG authored at frame size (pages/Livestream/*).
 *   - ads        — `customBgs[id] || customBgs.main || adsAssets[key].url`, a
 *     baked per-template asset (pages/Ads/*).
 *
 * Both fall back to their own asset, so "nothing seeded yet" still doesn't mean
 * "showing the catalog image" — which is why this is a channel fact and not
 * inferable from `customBgs` alone.
 */
const OWN_BACKGROUND_CHANNELS = new Set(["livestream", "ads"]);

/** @see OWN_BACKGROUND_CHANNELS */
export function channelRendersOwnBackgrounds(channel) {
  return OWN_BACKGROUND_CHANNELS.has(channel);
}

/** A numbered gallery background: the kind a banner can keep its own image
 * for, chain intact or not. Mirrors `isGalleryOption` in
 * layout/BgTransformer/templateBg.ts (duplicated rather than imported: libs/
 * stays framework- and layer-free of layout/). */
function isGalleryBgOption(bgOption) {
  const n = Number(bgOption);
  return Number.isInteger(n) && n > 0;
}

/**
 * Does `templateId` render the CATALOG's background image, or something else?
 *
 * The catalog transform is authored for the catalog's own artwork, so it is only
 * meaningful when that artwork is what's on screen. Four things displace it,
 * and this mirrors — condition for condition — how the page components,
 * `deriveBgUrl` and `BgTransformer` (via templateBg.ts's `resolveTemplateBackground`)
 * pick their `src`:
 *
 *   - the channel renders its own baked asset for every template (livestream,
 *     ads) — see OWN_BACKGROUND_CHANNELS. Both seed `customBgs` for EVERY
 *     template and fold it into `src` regardless of `locked`, so for them the
 *     channel is the whole answer.
 *   - `customBg` in custom MODE — the shared upload. `deriveBgUrl` returns it
 *     for `bgOption === "custom"` and nothing else, so its mere presence means
 *     nothing: a default scene can carry a baked `customBg` while the route
 *     displays a gallery variation (webinar's products.js scene does).
 *   - a LOCKED template's `customBgs[templateId]` — BgTransformer renders it as
 *     `isLocked && customBgs[id] ? customBgs[id] : src`, so unlocked the slot is
 *     inert and the catalog artwork is still what's on screen.
 *   - a PRESET for a numbered gallery background — `customBgs[templateId]` an
 *     admin saved into the default scene, which `resolveTemplateBackground`
 *     shows "chain intact or not" (templateBg.ts). A ready, exact-canvas-size
 *     per-template image (the IJ Figma backgrounds, COART-…) is exactly this:
 *     no `locked` state involved, yet it is never the catalog's artwork, so
 *     the catalog's crop — authored for a different image entirely — must not
 *     apply to it (it would zoom/pan an already-correct image, see
 *     feedback_bg_crop_catalog_vs_preset memory).
 *
 * The mirror has to be exact in both directions. Livestream is what this exists
 * for — its templates each render a baked SVG sized to the frame and must sit at
 * identity, but the catalog carries a crop for the 4600x9160 tileset artwork, and
 * applying it cropped the whole channel (issue #567). Over-reporting is just as
 * bad: reading a scene's baked `customBg` as "not the catalog image" dropped the
 * crop from every webinar banner, which then rendered at identity.
 */
export function templateShowsCatalogImage({
  channel,
  templateId,
  bgOption,
  customBg,
  customBgs,
  customBgUploaded,
  locked,
}) {
  if (channelRendersOwnBackgrounds(channel)) return false;
  if (String(bgOption) === "custom" && customBg) return false;
  const isLocked = Array.isArray(locked) ? locked.includes(templateId) : Boolean(locked);
  if (isLocked && customBgs?.[templateId]) return false;
  // A preset (not an upload) for a numbered background: resolveTemplateBackground
  // shows it chain intact or not, so an upload is the one `customBgs[id]` case
  // that stays inert — and the catalog artwork on screen — while unlocked.
  if (isGalleryBgOption(bgOption) && customBgs?.[templateId] && !customBgUploaded?.[templateId]) {
    return false;
  }
  return true;
}

/**
 * Compute the effective transform for one template by the precedence above.
 * Every input is optional; the first present source wins. Returns null when
 * nothing resolves (no user intent, no scene, no catalog) — the caller then
 * falls back to its own default (identity: natural size, top-left).
 *
 * @param {object}  args
 * @param {object=} args.userAdjustment  runtime transform, or null/undefined
 * @param {object=} args.resolvedScene   merged default scene (bgX/… records)
 * @param {object=} args.productScene    ALL-products only: this banner's product's
 *                                       own resolved default scene
 * @param {string=} args.baseTemplateId  ALL-products only: the canonical template
 *                                       id `productScene` is keyed by
 * @param {object=} args.catalogTransform runtime transform from the v3 catalog
 * @param {object=} args.legacyDefault    DEFAULT_BACKGROUND_DATA's entry for this
 *                                        template — pass it only when it applies
 *                                        (see catalogHasStoredTransforms)
 * @param {string}  args.templateId
 */
export function computeEffectiveBgTransform({
  userAdjustment,
  resolvedScene,
  productScene,
  baseTemplateId,
  catalogTransform,
  legacyDefault,
  templateId,
}) {
  const user = normalize(userAdjustment);
  if (user) return user;

  const fromScene = transformFromScene(resolvedScene, templateId);
  if (fromScene) return fromScene;

  // ALL-products: `templateId` is a `<template>-<product>` banner instance. The
  // ALL row above carries scoped keys only for the products that existed when an
  // admin last saved it, so a newer product finds nothing there — its own
  // resolved default scene, keyed canonically, is the layer the legacy aggregate
  // fanned out into those scoped keys (#567, R4).
  const fromProductScene = baseTemplateId
    ? transformFromScene(productScene, baseTemplateId)
    : null;
  if (fromProductScene) return fromProductScene;

  const fromCatalog = normalize(catalogTransform);
  if (fromCatalog) return fromCatalog;

  return normalize(legacyDefault);
}

/**
 * DEFAULT_BACKGROUND_DATA's crop for one template, in runtime shape.
 *
 * This is the pre-v3 fallback and nothing more: when a (product, variation) has
 * no transform scene, `useBackgrounds` spreads the table's whole x/y/scale
 * records into the context — `coords || DEFAULT_BACKGROUND_DATA` — so what a
 * template gets is one lookup in each record. The table covers ~12 template ids;
 * every other template read `undefined` and rendered at identity, which is why a
 * partial entry (Partners has y and scale but no x) returns null here too.
 *
 * Callers must apply this ONLY when the variation has no stored scene. Legacy
 * makes that choice per (product, variation), never per template: a scene that
 * simply omits a template leaves it at identity, it does not consult the table.
 */
export function legacyDefaultBgTransform(templateId) {
  return normalize({
    x: DEFAULT_BACKGROUND_DATA.x[templateId],
    y: DEFAULT_BACKGROUND_DATA.y[templateId],
    scale: DEFAULT_BACKGROUND_DATA.scale[templateId],
  });
}

/**
 * The transform a template is DISPLAYING: the derived one whole, or — with
 * derivation off — the legacy context records, field by field.
 *
 * Whole is the point. Under `bgDerivation` the context records stay empty (the
 * scene is never written into them), so any control that reads one directly
 * shows 0 while the banner renders something else: the rotate slider sat at 0 on
 * a WebpagePreview rotated 180 by its default scene, and moved nothing (#567).
 * Every reader — the renderer and the controls — goes through here.
 */
export function resolveDisplayedBgTransform({
  eff,
  fallbackEff,
  defaultTransform,
  templateId,
  fallbackTemplateId,
  bgX,
  bgY,
  bgScale,
  bgRotate,
  bgFlip,
}) {
  if (eff) return eff;
  if (fallbackEff) return fallbackEff;

  const templateDefault = normalize(defaultTransform) ?? {
    x: 0,
    y: 0,
    scale: 1,
    rotate: 0,
    flip: {...DEFAULT_FLIP},
  };

  const read = (record, defaultValue) =>
    record?.[templateId] ??
    (fallbackTemplateId ? record?.[fallbackTemplateId] : undefined) ??
    defaultValue;

  return {
    x: read(bgX, templateDefault.x),
    y: read(bgY, templateDefault.y),
    scale: read(bgScale, templateDefault.scale),
    rotate: read(bgRotate, templateDefault.rotate),
    flip: read(bgFlip, templateDefault.flip),
  };
}

/**
 * Split an ALL-products banner id (`<templateId>-<productCode>`) into its parts,
 * or null when it isn't one.
 *
 * `productCodes` is the authority rather than positional parsing: the suffix has
 * to be a product actually on this route, otherwise a template id that merely
 * contains a hyphen would be torn in half and resolved against a product that
 * isn't there. The longest matching code wins so a shorter code that happens to
 * be another's tail can't claim the id.
 */
export function splitAllProductsTemplateId(id, productCodes) {
  if (!id || !Array.isArray(productCodes)) return null;
  const byLongest = [...productCodes].sort((a, b) => String(b).length - String(a).length);
  for (const code of byLongest) {
    const suffix = `-${code}`;
    if (id.length > suffix.length && id.endsWith(suffix)) {
      return {templateId: id.slice(0, -suffix.length), productCode: code};
    }
  }
  return null;
}

/**
 * The background-variation key a template's transform must resolve under — the
 * variation the banner is DISPLAYING, which in ALL-products mode is the child
 * carousel's, not the parent route's (#567, R4 follow-up).
 *
 * Precedence:
 *   1. explicitBackground — the caller knows what it renders (BgTransformer
 *      passes its own provider's bgOption, the same value its edits are
 *      written under, so read and write can't diverge).
 *   2. perProductBg[instance.productCode] — ALL instances only: the carousel
 *      state, for callers outside the child providers (buildScene composition).
 *   3. parentBackground — single-product routes, and ALL instances before the
 *      carousel has initialized (matching the parent is the pre-init display).
 *
 * Always stringified: adjustment records and catalog maps are keyed by string,
 * while settings carry numbers or strings depending on the writer.
 */
export function effectiveBgOptionKey({explicitBackground, instance, perProductBg, parentBackground}) {
  if (explicitBackground !== undefined && explicitBackground !== null) {
    return String(explicitBackground);
  }
  const fromCarousel = instance ? perProductBg?.[instance.productCode] : undefined;
  if (fromCarousel !== undefined && fromCarousel !== null) return String(fromCarousel);
  return String(parentBackground);
}

/**
 * A catalog item's crop for one template, resolved for a product: the product's
 * own `productTransforms` entry when the ALL response carries one, else the
 * shared `transforms` map.
 *
 * Only an ALL response has `productTransforms`, and only for products with their
 * own transform source — for every other product the shared map already IS that
 * product's resolution (see BgCatalogItem.productTransforms).
 */
export function catalogTransformForTemplate(catalogItem, templateId, productCode) {
  if (!catalogItem) return undefined;
  const own = productCode ? catalogItem.productTransforms?.[productCode]?.[templateId] : undefined;
  return own ?? catalogItem.transforms?.[templateId];
}

/**
 * Did a stored transform scene back this variation for this product?
 *
 * Answers the question `legacyDefaultBgTransform` is gated on, and mirrors
 * `catalogTransformForTemplate`'s source selection condition for condition: an
 * ALL response lists a product in `productTransforms` only when it HAS its own
 * source, so that presence is itself a yes; anyone else resolves the shared map,
 * whose provenance is the item's own `hasStoredTransforms`.
 *
 * Deliberately not inferred from an empty `transforms` map: a stored scene only
 * carries the templates of the page it was saved from, so "empty" conflates "no
 * scene" (→ the legacy table) with "a scene that crops nothing here" (→ identity).
 */
export function catalogHasStoredTransforms(catalogItem, productCode) {
  if (!catalogItem) return false;
  if (productCode && catalogItem.productTransforms?.[productCode]) return true;
  return Boolean(catalogItem.hasStoredTransforms);
}

/**
 * Build a legacy-shaped `allBgUrls` override from the v3 catalog (issue #562, M3)
 * so `useBackgrounds` can skip Kraken `get_tilesets` — the same mechanism the
 * Ads/GDN channel already uses via `backgroundsOverride`.
 *
 * `catalogBackgrounds` is the resolved-for-product list from GET /api/v3/backgrounds
 * (each item already carries the product's DISPLAY `imageUrl` + `-mini`/`-outdated`
 * variants). The result is a dense array indexed by `variation - 1`, each entry
 * the url list for that variation — so the existing `findBgUrl`/`deriveBgUrl`
 * select `/<product>` / `/<product>-mini` / `/<product>-outdated` unchanged (the
 * display urls already contain `/<product>`; only the never-displayed `#<hash>`
 * default carries a `#`, and it is not here). Returns null when there's nothing
 * to build (caller decides whether to fall back to `get_tilesets`).
 *
 * ALL-products mode (releases "What's New") is the exception: the view renders
 * one banner per product, each resolving its own `/<product>` out of this same
 * product-agnostic array, so a single resolved-for-ALL url would leave every
 * banner and every selector thumbnail blank. The server ships `productImages`
 * (every product's display image) for that request, and each variation's entry
 * is then the full url list — exactly the shape `get_tilesets` returned.
 *
 * A draft upload (a synthetic variation, see bgIdentity.ts) is left out: its
 * number is far too large for an array, and its url resolves through the
 * catalog index instead.
 */
export function buildBackgroundsOverride(catalogBackgrounds) {
  if (!Array.isArray(catalogBackgrounds) || catalogBackgrounds.length === 0) return null;
  const listed = catalogBackgrounds.filter(
    (b) => Number.isInteger(b.variation) && b.variation >= 1 && !isDraftUploadVariation(b.variation)
  );
  const maxVariation = Math.max(0, ...listed.map((b) => b.variation));
  const arr = Array.from({length: maxVariation}, () => []);
  for (const b of listed) {
    arr[b.variation - 1] = b.productImages
      ? Object.values(b.productImages).flatMap((img) =>
          [img.url, img.miniUrl, img.outdatedUrl].filter(Boolean)
        )
      : [b.imageUrl, b.miniUrl, b.outdatedUrl].filter(Boolean);
  }
  return arr;
}

/**
 * Compose the per-template bg-transform records a saved scene carries
 * (bgX/bgY/bgScale/bgRotate/bgFlip) from the effective-transform selector
 * (issue #528, 2a — render-path rewire).
 *
 * `getEffectiveTransform(id)` returns the derived {x,y,scale,rotate,flip} for
 * one template, or null when nothing resolves. Only ids with a resolved
 * transform contribute — a null id is omitted from every record, so a template
 * with no transform round-trips as "absent", exactly as the legacy path left it
 * unset (buildScene must produce the same shape it always did). Returns the five
 * records ready to spread onto the scene object.
 */
export function composeSceneBgTransforms(templateIds, getEffectiveTransform) {
  const bgX = {};
  const bgY = {};
  const bgScale = {};
  const bgRotate = {};
  const bgFlip = {};
  for (const id of templateIds || []) {
    const t = getEffectiveTransform(id);
    if (!t) continue;
    bgX[id] = t.x;
    bgY[id] = t.y;
    bgScale[id] = t.scale;
    bgRotate[id] = t.rotate;
    bgFlip[id] = t.flip;
  }
  return {bgX, bgY, bgScale, bgRotate, bgFlip};
}
